import { Check, Minus } from "lucide-react";
import { Fragment, useState } from "react";
import {
  PLAN_MATRIX_TIERS,
  PLAN_MATRIX_GROUPS,
  matrixRowSlug,
} from "../lib/planMatrix";

// Shared tier × feature comparison table. Rendered identically beneath the
// Billing plan cards and on the Landing pricing section so both surfaces tell
// the exact same story (and stay in lock-step with backend/plans.py).

function MatrixCell({ value, accent }) {
  if (value === true) {
    return (
      <Check
        className="w-4 h-4 mx-auto"
        style={{ color: accent }}
        data-testid="matrix-cell-yes"
      />
    );
  }
  if (value === false) {
    return (
      <Minus
        className="w-4 h-4 mx-auto text-neutral-300"
        data-testid="matrix-cell-no"
      />
    );
  }
  return (
    <span
      className="text-xs font-bold text-neutral-800"
      data-testid="matrix-cell-text"
    >
      {value}
    </span>
  );
}

export function PlanComparisonMatrix({ className = "" }) {
  const [cycle, setCycle] = useState("monthly"); // "monthly" | "annual"
  const annual = cycle === "annual";
  return (
    <section className={className} data-testid="plan-comparison-matrix">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
          Compare every tier
        </div>
        <div
          className="inline-flex border border-neutral-900"
          data-testid="matrix-cycle-toggle"
          role="tablist"
          aria-label="Billing cycle"
        >
          {[
            { key: "monthly", label: "Monthly" },
            { key: "annual", label: "Annual · save a month" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="tab"
              aria-selected={cycle === opt.key}
              data-testid={`matrix-cycle-${opt.key}`}
              onClick={() => setCycle(opt.key)}
              className={`h-9 px-4 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
                cycle === opt.key
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto border border-neutral-900">
        <table className="w-full min-w-[920px] border-collapse text-left">
          <thead>
            <tr className="bg-neutral-900 text-white">
              <th className="py-3 px-4 text-[11px] uppercase tracking-[0.2em] font-bold">
                Feature
              </th>
              {PLAN_MATRIX_TIERS.map((t, ti) => {
                const firstAdvisor = t.advisor && !PLAN_MATRIX_TIERS[ti - 1]?.advisor;
                return (
                  <th
                    key={t.key}
                    data-testid={`matrix-tier-${t.key}`}
                    className={`py-3 px-4 text-center ${firstAdvisor ? "border-l-2 border-white/30" : ""}`}
                  >
                    {t.advisor && (
                      <div className="text-[8px] uppercase tracking-[0.25em] font-bold text-[#A78BFA] mb-0.5">
                        Advisor
                      </div>
                    )}
                    <div
                      className="text-[11px] uppercase tracking-[0.2em] font-bold"
                      style={{ color: t.featured ? "#C9B8E8" : "#FFFFFF" }}
                    >
                      {t.label}
                    </div>
                    <div
                      className="text-xs text-white/70 font-semibold mt-0.5"
                      data-testid={`matrix-price-${t.key}`}
                    >
                      {annual ? `${t.priceAnnual}/yr` : `${t.priceMonthly}/mo`}
                    </div>
                    {annual && (
                      <div
                        className="text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-300 mt-0.5"
                        data-testid={`matrix-save-${t.key}`}
                      >
                        {t.annualSave}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {PLAN_MATRIX_GROUPS.map((group) => (
              <Fragment key={group.label}>
                <tr
                  className="bg-neutral-100"
                  data-testid={`matrix-group-${matrixRowSlug(group.label)}`}
                >
                  <td
                    colSpan={1 + PLAN_MATRIX_TIERS.length}
                    className="py-2 px-4 text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-600"
                  >
                    {group.label}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr
                    key={row.feature}
                    data-testid={`matrix-row-${matrixRowSlug(row.feature)}`}
                    className="border-t border-neutral-200"
                  >
                    <td className="py-2.5 px-4 text-sm text-neutral-800">
                      {row.feature}
                    </td>
                    {PLAN_MATRIX_TIERS.map((t, ti) => {
                      const firstAdvisor = t.advisor && !PLAN_MATRIX_TIERS[ti - 1]?.advisor;
                      return (
                        <td
                          key={t.key}
                          className={`py-2.5 px-4 text-center ${firstAdvisor ? "border-l-2 border-neutral-200" : ""}`}
                        >
                          <MatrixCell value={row[t.key]} accent={t.accent} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
